<script>
    import { slide, fade } from 'svelte/transition';
	import { sineOut } from 'svelte/easing';

    import { getDisplayType } from '../../trigger-date.js';
    import { vptServer, token, inmateDrawerOpen } from '../../stores.js';
    import { sortByProperty } from '../../sort.js';
    import { getIsoDateStringAsDate, isDateInPast, isToday, isDateInFuture,
             isDateWithinFuturePeriod, getDaysDifference, isDateWithinPastPeriod } from '../../date-time.js';

    import TriggerDates from './trigger-dates/TriggerDates.svelte';
    import TriggerDatesWidget from './trigger-dates/TriggerDatesWidget.svelte';
    import ContactNotes from './contact/ContactNotes.svelte';
    import ContactNotesWidget from './contact/ContactNotesWidget.svelte';
    
    export let id;
    export let top;
    export let todaysConfig;
 
    let triggerDates;
    let contactNotes;
    let unseenDays;

    let showTriggerDateWidget = true;
    let showContactNotesWidget = true;

    $: {
        if (todaysConfig) {
            triggerDates = null;
            contactNotes = null;
            getTriggerDates();
            if (todaysConfig.contactTypeId) {
                getContactHistory();
            }
        }
    }

    async function getTriggerDates() {
        await fetch(`${$vptServer}/inmate/${id}/trigger-dates`, {
            headers: {
                "Authorization": "Bearer " + $token
            }
        })
        .then(r => {
            if (!r.ok) {
                throw Error(r.status);
            }
            return r;
        })
        .then(r => r.json())
        .then(data => {
            let currentYear = new Date().getFullYear();
            let triggerDatesMapped = data.map(triggerDate => {
                let triggerDateAnnual = getIsoDateStringAsDate(triggerDate.date);
                triggerDateAnnual.setFullYear(currentYear);
                return {...triggerDate, 
                    annualDate: triggerDateAnnual, 
                    ...getRelevantDateProps(triggerDateAnnual),
                    daysFromToday: getDaysDifference(new Date(), triggerDateAnnual),
                    displayType: getDisplayType(triggerDate.type)};
            });
            triggerDatesMapped.sort(sortByProperty("annualDate", false));
            triggerDates = triggerDatesMapped;
        })
        .catch(error => {
            console.error("Error getting trigger dates history");
            console.error(error);
            return;
        });
    }

    // Returns whether a given trigger date is in the relevant previous or upcoming date period. 
    function getRelevantDateProps(triggerDate) {
        if (isDateInPast(triggerDate)) {
            if (isDateWithinPastPeriod(triggerDate, todaysConfig.triggerDatesPastDaysToAnalyse)) {
                return {previous: true, upcoming: false}
            } else  {
                return {previous: false, upcoming: false};
            }
        } else {
            if (isDateInFuture(triggerDate) && isDateWithinFuturePeriod(triggerDate, todaysConfig.triggerDatesFutureDaysToAnalyse) 
                    || isToday(triggerDate)) {
                return {previous: false, upcoming: true};
            } else {
                return {previous: false, upcoming: false};
            }
        }
    }

    async function getContactHistory() {
        await fetch(`${$vptServer}/inmate/${id}/contact/list`, {
            headers: {
                "Authorization": "Bearer " + $token
            }
        })
        .then(r => {
            if (!r.ok) {
                throw Error(r.status);
            }
            return r;
        })
        .then(r => r.json())
        .then(data => {
            data.sort(sortByProperty("contactDateTime", true));
            contactNotes = data;
            getLatestContact();
        })
        .catch(error => {
            console.error("Error getting contact history");
            console.error(error);
            return;
        });
    }

    async function getLatestContact() {
        await fetch(`${$vptServer}/inmate/${id}/contact/latest`, {
            headers: {
                "Authorization": "Bearer " + $token
            }
        })
        .then(r => {
            if (!r.ok) {
                throw Error(r.status);
            }
            return r;
        })
        .then(r => r.json())
        .then(data => {
            let latestContact = data;
            unseenDays = data.targetedUnseenDays;
            
            // Map the latest contact data to the correct` contact notes
            if (latestContact && latestContact.contactId) {
                contactNotes = contactNotes.map(contactNote =>
                    contactNote.contactId === latestContact.contactId
                        ? { ...contactNote, 
                            isolationIncreased: latestContact.indicatorIsolationIncreased,
                            targetedTotalIncreased: latestContact.indicatorTargetedTotalIncreased,
                            overdueDays: latestContact.actionOverdueDays }
                        : contactNote
                );
            }
        })
        .catch(error => {
            console.error("Error getting latest contact");
            console.error(error);
            return;
        });
    }

    function reloadContacts() {
        contactNotes = null;
        getContactHistory();
    }
</script>


<style>
    #inmate-drawer-container {
        position: fixed;
        z-index: 1031;
        right: 15px;
        bottom: 65px;
        background-color: white;
        border: 1px solid rgba(0,0,0,.125);
        border-radius: 5px;
    }

    button.drawer-control {
        position: absolute;
        top: 10px;
        left: -12px;
        height: 24px;
        width: 24px;
        display: flex;
        justify-content: center;
        align-items: center;
        background-color: white;
        border: 1px solid rgba(0,0,0,.125);
        border-radius: 50%;
    }

    button.drawer-control:hover {
        background-color: #efefef;
    }

    button.drawer-control .bi {
        margin-bottom: 4px;
        color: #0267B3;
    }

    #inmate-drawer, #inmate-drawer-collapsed {
        height: 100%;
        overflow: auto;
    }
    
    #inmate-drawer {
        width: 240px;
    }

    #inmate-drawer-collapsed {
        width: 66px;
    }

    .drawer-section {
        padding: 10px 15px;
    }

    .drawer-section:not(:first-child) {
        border-top: 1px solid rgba(0,0,0,.125);;
    }

    .drawer-header {
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        align-items: center;
        width: 100%;
        margin-bottom: 10px;
        color: #0267B3;
    }

    .drawer-header .drawer-label {
        display: flex;
        align-items: center;
        margin-left: 1px;
    }

    .drawer-header .drawer-label .drawer-label-title {
        font-size: 14px;
        font-weight: 600;
    }

    .drawer-header .drawer-label .drawer-label-count {
        display: inline-flex;
        height: 18px;
        width: 18px;
        border-radius: 50%;
        align-items: center;
        justify-content: center;
        font-size: 10px;
        font-weight: 400;
        background-color: #efefef;
        color: #0267B3;
        margin-left: 5px;
    }

    .drawer-header .drawer-header-chevron {
        margin-bottom: 4px;
    }

    .toggle-display-widget-only {
        margin-top: 5px;
        color: #0267B3;
        font-size: 11px;
        width: 100%;
        text-align: center;
    }

    button {
        white-space: nowrap;
        overflow: hidden;
    }
</style>

<div id="inmate-drawer-container" style={`top:${top}px`}>
    <button class="button-reset focus-outline drawer-control" on:click={() => inmateDrawerOpen.set(!($inmateDrawerOpen))}>
        {#if $inmateDrawerOpen}
            <i class="bi bi-chevron-right"></i>
        {:else}
            <i class="bi bi-chevron-left"></i>
        {/if}
    </button>

    {#if $inmateDrawerOpen}
        <div id="inmate-drawer" transition:slide|local={{ delay: 200, duration: 400, axis: 'x', easing: sineOut }}>
            <div id="drawer-fade-container" in:fade|local={{ delay: 400, duration: 500 }} out:fade|local={{ duration: 400 }}>
                
                <div class="drawer-section">
                    <button class="button-reset focus-outline drawer-header" on:click={() => showTriggerDateWidget = !showTriggerDateWidget}>
                        <div class="drawer-label">
                            <span class="drawer-label-title">
                                {todaysConfig.triggerDatesLabelName}
                            </span>
                            {#if triggerDates}<span class="drawer-label-count">{triggerDates.length}</span>{/if}
                        </div>
                        
                        {#if showTriggerDateWidget}
                            <i class="bi bi-chevron-down drawer-header-chevron"></i>
                        {:else}
                            <i class="bi bi-chevron-up drawer-header-chevron"></i>
                        {/if}
                    </button>
                
                    {#if showTriggerDateWidget}
                        <div transition:slide|local={{axis: 'y', duration: 200}}>
                            <TriggerDatesWidget {triggerDates} displayDateOnly={false} id={'td-widget-full'}
                                pastDaysToAnalyse={todaysConfig.triggerDatesPastDaysToAnalyse}
                                futureDaysToAnalyse={todaysConfig.triggerDatesFutureDaysToAnalyse}  />
                        </div>
                    {:else}
                        <div transition:slide|local={{axis: 'y', duration: 200}}>
                            <TriggerDates {triggerDates} />
                        </div>
                    {/if}

                    {#if triggerDates}
                        <button on:click={() => showTriggerDateWidget = !showTriggerDateWidget} class="button-reset focus-outline toggle-display-widget-only">
                            {#if showTriggerDateWidget}Show All{:else}Show Recent / Upcoming{/if}
                        </button>
                    {/if}
                </div>

                {#if todaysConfig.contactTypeId}
                    <div class="drawer-section">
                        <button class="button-reset focus-outline drawer-header" on:click={() => {if (contactNotes && contactNotes.length > 1) {showContactNotesWidget = !showContactNotesWidget}}}>
                            <div class="drawer-label">
                                <span class="drawer-label-title">
                                    Contacts
                                </span>
                                {#if contactNotes}<span class="drawer-label-count">{contactNotes.length}</span>{/if}
                            </div>

                            {#if contactNotes && contactNotes.length > 1}
                                {#if showContactNotesWidget}
                                    <i class="bi bi-chevron-down drawer-header-chevron"></i>
                                {:else}
                                    <i class="bi bi-chevron-up drawer-header-chevron"></i>
                                {/if}
                            {/if}
                        </button>

                        {#if showContactNotesWidget}
                            <div transition:slide|local={{axis: 'y', duration: 200}}>
                                <ContactNotesWidget inmateUrn={id} {contactNotes} {unseenDays} inmateLabel={todaysConfig.inmateLabelName}
                                    displayDateOnly={false} on:update={reloadContacts} id={'contact-widget-full'} />
                                
                                {#if contactNotes && contactNotes.length > 1}
                                    <button on:click={() => showContactNotesWidget = false} class="button-reset focus-outline toggle-display-widget-only">
                                        Show More
                                    </button>
                                {/if}
                            </div>
                        {:else}
                            <div transition:slide|local={{axis: 'y', duration: 200}}>
                                <ContactNotes inmateUrn={id} {contactNotes} on:update={reloadContacts} />
                            </div>
                        {/if}
                    </div>
                {/if}
            </div>
        </div>
    {:else}
        <div id="inmate-drawer-collapsed" in:fade|local={{ delay: 600, duration: 200}} out:fade|local={{ duration: 300 }}>
            <div class="drawer-section">
                <TriggerDatesWidget {triggerDates} displayDateOnly={true} id={'td-widget-collapsed'}
                        pastDaysToAnalyse={todaysConfig.triggerDatesPastDaysToAnalyse}
                        futureDaysToAnalyse={todaysConfig.triggerDatesFutureDaysToAnalyse} />
            </div>

            {#if todaysConfig.contactTypeId}
                <div class="drawer-section">
                    <ContactNotesWidget inmateUrn={id} {contactNotes} {unseenDays} displayDateOnly={true} 
                        on:update={reloadContacts} id={'contact-widget-collapsed'} />
                </div>
            {/if}
        </div>
    {/if}
</div>